import { useCallback } from 'react';
import { Bookmark, BookOpen, Star, Archive, AlertCircle, Share, Download } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { AddLinkDialog } from './AddLinkDialog';
import { getBookmarkletCode } from '@/lib/bookmarklet';

// Simple mobile detection (with ?mobile=true override for testing)
function getIsMobile() {
  const searchParams = new URLSearchParams(window.location.search);
  return (
    searchParams.get('mobile') === 'true' || /iPhone|iPad|iPod|Android/i.test(navigator.userAgent)
  );
}

type EmptyStateType = 'all' | 'unread' | 'favorites' | 'archived' | 'error';

interface EmptyStateProps {
  type: EmptyStateType;
  error?: Error;
  onRetry?: () => void;
}

const emptyStates: Record<
  Exclude<EmptyStateType, 'error'>,
  { icon: typeof Bookmark; title: string; description: string }
> = {
  all: {
    icon: Bookmark,
    title: 'No links yet',
    description: 'Save your first link to get started',
  },
  unread: {
    icon: BookOpen,
    title: 'All caught up!',
    description: "You've read all your saved links",
  },
  favorites: {
    icon: Star,
    title: 'No favorites',
    description: 'Star links you want to find easily later',
  },
  archived: {
    icon: Archive,
    title: 'No archived links',
    description: 'Archive links to keep your list tidy',
  },
};

export function EmptyState({ type, error, onRetry }: EmptyStateProps) {
  if (type === 'error') {
    return (
      <div className="flex flex-col items-center justify-center py-16">
        <AlertCircle className="h-12 w-12 text-destructive/50" />
        <h3 className="mt-4 text-lg font-medium">Failed to load links</h3>
        <p className="mt-1 max-w-sm text-center text-sm text-muted-foreground">
          {error?.message || 'An unexpected error occurred'}
        </p>
        {onRetry && (
          <Button variant="outline" className="mt-4" onClick={onRetry}>
            Try again
          </Button>
        )}
      </div>
    );
  }

  const state = emptyStates[type];
  const Icon = state.icon;

  // React blocks javascript: URLs — set href directly on the DOM element
  const bookmarkletRef = useCallback((node: HTMLAnchorElement | null) => {
    if (node) node.setAttribute('href', getBookmarkletCode());
  }, []);

  // For empty "all" list, show onboarding instructions
  if (type === 'all') {
    return (
      <div className="flex flex-col items-center justify-center py-12 px-6">
        <Icon className="h-12 w-12 text-muted-foreground/50" />
        <h3 className="mt-4 text-lg font-medium">{state.title}</h3>
        <p className="mt-1 text-sm text-muted-foreground">{state.description}</p>

        <div className="mt-8 w-full max-w-sm space-y-4">
          {getIsMobile() ? (
            // Mobile: PWA install instructions
            <>
              <div className="rounded-lg bg-muted/50 p-4">
                <div className="flex items-center gap-2 font-medium">
                  <Download className="h-4 w-4" />
                  Install Pith for easy saving
                </div>
                <ol className="mt-3 space-y-2 text-sm text-muted-foreground">
                  <li className="flex items-start gap-2">
                    <span className="font-medium text-foreground">1.</span>
                    <span>
                      Tap <Share className="inline h-4 w-4" /> in your browser
                    </span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="font-medium text-foreground">2.</span>
                    <span>Select "Add to Home Screen"</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="font-medium text-foreground">3.</span>
                    <span>Share articles to Pith from any app</span>
                  </li>
                </ol>
              </div>
              <div className="text-center">
                <Link to="/bookmarklet">
                  <Button variant="outline" size="sm">
                    More options
                  </Button>
                </Link>
              </div>
            </>
          ) : (
            // Desktop: Bookmarklet instructions
            <>
              <div className="rounded-lg bg-muted/50 p-4">
                <div className="flex items-center gap-2 font-medium">
                  <Bookmark className="h-4 w-4" />
                  Install the bookmarklet
                </div>
                <p className="mt-2 text-sm text-muted-foreground">
                  Drag this button to your bookmarks bar:
                </p>
                <div className="mt-3 flex justify-center">
                  <a
                    ref={bookmarkletRef}
                    href="#"
                    onClick={(e) => e.preventDefault()}
                    draggable
                    className="inline-flex h-9 items-center justify-center gap-2 rounded-md bg-primary px-3 text-sm font-medium text-primary-foreground shadow transition-colors hover:bg-primary/90"
                  >
                    <Bookmark className="mr-2 h-4 w-4" />
                    Save to Pith
                  </a>
                </div>
                <p className="mt-2 text-center text-xs text-muted-foreground">
                  <Link to="/bookmarklet" className="underline hover:text-foreground">
                    Need help?
                  </Link>
                </p>
              </div>
              <div className="text-center text-sm text-muted-foreground">
                Or add a link manually:
              </div>
              <div className="flex justify-center">
                <AddLinkDialog />
              </div>
            </>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col items-center justify-center py-16">
      <Icon className="h-12 w-12 text-muted-foreground/50" />
      <h3 className="mt-4 text-lg font-medium">{state.title}</h3>
      <p className="mt-1 text-sm text-muted-foreground">{state.description}</p>
    </div>
  );
}
